import { useParams } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { localStorageService } from '@/services/localStorage.service';

/**
 * Returns the currently active chatbot ID with the following priority:
 *
 * 1. `:id` URL param — when the user is on a chatbot-specific route (e.g. /chatbot/:id/whatsapp)
 * 2. localStorage `chatbotId` — set by ChatbotBuilderLayout on chatbot switch; also synced
 *    by AuthProvider.initializeAuth() on first login so it is always the canonical source
 * 3. user.user.chatbotId — absolute last resort: only populated before initializeAuth has
 *    had a chance to write to localStorage (i.e. within the first render on fresh login)
 *
 * The literal string "undefined" is explicitly filtered out to prevent Mongoose ObjectId
 * cast errors on the backend when chatBotId hasn't resolved yet.
 */
export function useChatbotId(): string {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const resolved =
    id ||
    localStorageService.getChatbotId() ||
    user?.user?.chatbotId ||
    '';

  // Guard: never return the literal string "undefined" — treat it as missing
  if (!resolved || resolved === 'undefined') return '';

  return resolved;
}
